import React, { PropsWithChildren, useState } from "react";
import { fullMonths, months } from "utils/common/date";
import useTheme from "evcharging/hooks/useTheme";
import { css } from "@emotion/css";
import { Theme } from "evcharging/app/themes";
import Box from "evcharging/components/Box";

interface RatesMonthCalendarProps {
  value: string;
  minDate: Date;
  onChange: (value: string) => void;
}

const useRetailMonthCalendar = (props: PropsWithChildren<RatesMonthCalendarProps>) => {
  const { onChange, minDate = new Date() } = props;
  const minYear = minDate.getFullYear();
  const [year, setYear] = useState<number>(minYear);

  const handleSelectMonth = (idx: number) => {
    onChange(`${fullMonths[idx]}, ${year}`);
  };

  const handlePrevYear = () => {
    setYear(year - 1);
  };

  const handleNextYear = () => {
    setYear(year + 1);
  };

  return {
    ...props,
    year,
    minYear,
    minDate,
    handleSelectMonth,
    handlePrevYear,
    handleNextYear,
  };
};

const RatesMonthCalendar: React.FC<RatesMonthCalendarProps> = (props) => {
  const { year, minYear, minDate, handleSelectMonth, handlePrevYear, handleNextYear } =
    useRetailMonthCalendar(props);

  const theme = useTheme();
  const classes = createStyles(theme);

  return (
    <>
      <Box display={'flex'} className={classes.yearActions}>
        <button tabIndex={-1} className={classes.yearButton} onClick={handlePrevYear} disabled={year === minYear}>
          &lt;
        </button>
        <div className={classes.yearValue}>{year}</div>
        <button tabIndex={-1} className={classes.yearButton} onClick={handleNextYear}>
          &gt;
        </button>
      </Box>

      <Box className={classes.years}>
        {months.map((monthItem, idx) => {
          return (
            <button
              className={classes.month}
              onClick={() => handleSelectMonth(idx)}
              disabled={
                new Date(`${monthItem}, ${year}`).getMonth() < minDate.getMonth() &&
                year === minYear
              }
            >
              {monthItem}
            </button>
          );
        })}
      </Box>
    </>
  );
};

const createStyles = (theme: Theme) => ({
  years: css({
    paddingBottom: 8,
    display: 'flex',
    flexWrap: 'wrap',
  }),
  month: css({
    width: '33%',
    border: 'none',
    background: 'none',
    textAlign: 'center',
    fontSize: theme.spacing(2),
    fontWeight: 600,
    borderRadius: 4,
    padding: 2,
    minWidth: 'auto',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#fff',
    },
  }),
  yearValue: css({
    fontWeight: 700,
    color: '#3a3a3a',
  }),
  yearActions: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  yearButton: css({
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    borderRadius: theme.spacing(0.5),
    padding: 0,
    margin: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#fff',
    },
  }),
});

export default RatesMonthCalendar;
